import { DOCUMENT } from '@angular/common';
import { HttpClient, HttpContext } from '@angular/common/http';
import { DestroyRef, effect, inject, Injectable, Injector, signal, untracked } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AuthService } from '@frontendlib/common/services/auth.service';
import { CurrentUserStore } from '@frontendlib/common/services/current-user.store';
import { DeviceSessionsService } from '@frontendlib/common/services/device-sessions.service';
import { IGNORE_UNAUTHORIZED_REDIRECT } from '@frontendlib/common/utils/auth';
import { environment } from '@environment';
import {
  createDesktopNotificationPresenter,
  defineNotificationPlugin,
  NotificationCoordinator,
  type NotificationCandidate,
  type NotificationPresenter,
  type NotificationSession,
} from '@frontendlib/notifications';
import type { NotificationFeedLiveEvent } from '@sharedlib/notification-contract';
import { firstValueFrom, fromEvent, takeUntil, timeout } from 'rxjs';
import { io } from 'socket.io-client';
import { HostedNotificationActionsService } from './hosted-notification-actions.service';
import { HostedNotificationViewService } from './hosted-notification-view.service';
import { NotificationFeedClient, type HostedNotificationSocket } from './notification-feed-client';
import {
  notificationDate,
  notificationKeys,
  notificationRecord,
  notificationUuid,
  parseNotificationLiveEvent,
} from './notification-feed.validation';

const RETRY_MIN_MS = 15_000;
const RETRY_MAX_MS = 120_000;
const NATIVE_NEGOTIATION_TIMEOUT_MS = 2_000;

function boundedNativeOperation<T>(work: Promise<T>): Promise<T> {
  let timer: ReturnType<typeof setTimeout> | undefined;
  return Promise.race([
    work,
    new Promise<never>((_resolve, reject) => {
      timer = setTimeout(
        () => reject(new Error('Native notification operation unavailable')),
        NATIVE_NEGOTIATION_TIMEOUT_MS,
      );
    }),
  ]).finally(() => clearTimeout(timer));
}

function boundedNativeSession(session: NotificationSession, unavailable: () => void): NotificationSession {
  return {
    show: async (command) => {
      let timedOut = false;
      const dismiss = (): void => {
        void boundedNativeOperation(session.dismiss([command.candidate.id])).catch(() => undefined);
      };
      const work = session.show(command).then((result) => {
        if (timedOut && result.status === 'accepted') dismiss();
        if (result.status === 'stale' || result.status === 'rate-limited') unavailable();
        return result;
      });
      try {
        return await boundedNativeOperation(work);
      } catch (error) {
        timedOut = true;
        dismiss();
        throw error;
      }
    },
    updateContext: (revision) => boundedNativeOperation(session.updateContext(revision)),
    onActivated: (listener) => session.onActivated(listener),
    acknowledge: (eventId) => boundedNativeOperation(session.acknowledge(eventId)),
    dismiss: (ids) => boundedNativeOperation(session.dismiss(ids)),
    clearScope: (scopeId) => boundedNativeOperation(session.clearScope(scopeId)),
    close: () => boundedNativeOperation(session.close()),
  };
}

/** Application-root composition, separate from Chat's socket and ordinary browser permissions. */
@Injectable({ providedIn: 'root' })
export class HostedNotificationsService {
  private readonly document = inject(DOCUMENT);
  private readonly http = inject(HttpClient);
  private readonly auth = inject(AuthService);
  private readonly currentUser = inject(CurrentUserStore);
  private readonly devices = inject(DeviceSessionsService);
  private readonly view = inject(HostedNotificationViewService);
  private readonly injector = inject(Injector);
  private readonly destroyRef = inject(DestroyRef);
  private readonly authIdentity = signal<{ accountId: string; exp: number } | null>(null);
  private readonly listeners = new Set<(candidate: unknown) => void>();
  private readonly cursors = new Map<string, string>();
  private readonly desktopPresenter = createDesktopNotificationPresenter(() => {
    const window = this.document.defaultView;
    return window ? Reflect.get(window, 'stitchDesktop') : undefined;
  });
  private seed: NotificationSession | null = null;
  private readonly presenter: NotificationPresenter = {
    open: async () => {
      const seed = this.seed;
      this.seed = null;
      return seed ?? this.openNative();
    },
  };
  private readonly coordinator = new NotificationCoordinator({
    presenter: this.presenter,
    // The signed, current-policy Gateway projection is the delivery-policy authority. The host
    // does not invent a second preference cache; identity and format are checked independently.
    policy: {
      allows: (candidate, operation) =>
        !operation.signal.aborted &&
        this.identityCurrent() &&
        parseNotificationLiveEvent(candidate.payload)?.accountId === candidate.scopeId,
    },
  });
  private lifetime = new AbortController();
  private feed: NotificationFeedClient | null = null;
  private epoch = 0;
  private nativeIdentityEpoch = 0;
  private nativeSessionEpoch = 0;
  private nativeAccounts: readonly string[] | null = null;
  private key: string | null = null;
  private retryDelay = RETRY_MIN_MS;
  private retryTimer: ReturnType<typeof setTimeout> | undefined;
  private leaseTimer: ReturnType<typeof setTimeout> | undefined;
  private readyTimer: ReturnType<typeof setTimeout> | undefined;
  private destroyed = false;

  public constructor() {
    const plugin = defineNotificationPlugin<NotificationFeedLiveEvent>({
      source: 'chat',
      category: 'direct-message',
      isPayload: (value): value is NotificationFeedLiveEvent => parseNotificationLiveEvent(value) !== null,
      format: (event) => ({
        title: event.title,
        // #1447 NT1: the message text while the person shows it, else what kind of notification it is.
        body: event.preview ?? event.contextLabel ?? 'New direct message',
        silent: !event.sound,
        ...(event.viewKey ? { viewKey: event.viewKey } : {}),
      }),
      activate: (event, operation) =>
        this.injector.get(HostedNotificationActionsService).activate(event.accountId, event.deliveryId, operation),
    });
    this.coordinator.register({
      plugin,
      source: {
        subscribe: (listener, signal) => {
          if (signal.aborted) return () => undefined;
          this.listeners.add(listener);
          const unsubscribe = (): void => {
            this.listeners.delete(listener);
          };
          signal.addEventListener('abort', unsubscribe, { once: true });
          // Removing a native consumer must never close the host-owned socket.
          return () => {
            signal.removeEventListener('abort', unsubscribe);
            unsubscribe();
          };
        },
      },
    });
    this.auth
      .getAuthenticated()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((payload) => {
        const identity =
          payload && notificationUuid(payload.accountId) ? { accountId: payload.accountId, exp: payload.exp } : null;
        // Auth emissions are synchronous; a batched Angular effect must not hide a sign-out followed
        // by a same-account sign-in in one turn.
        if (!identity || (this.key !== null && identity.accountId !== this.key)) {
          this.key = null;
          this.invalidate(true);
        }
        this.authIdentity.set(identity);
      });
    this.devices.identityChanged$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.key = null;
      this.invalidate(true);
    });
    effect(() => {
      const identity = this.authIdentity();
      const selected = this.currentUser.accountId();
      const key = identity && identity.accountId === selected ? identity.accountId : null;
      untracked(() => {
        if (key === this.key) return;
        this.key = key;
        this.invalidate(true);
        if (key) {
          const epoch = this.epoch;
          void this.connect(epoch).catch(() => this.retry(epoch));
        }
      });
    });
    effect(() => {
      const view = this.view.notificationView();
      untracked(() => this.coordinator.updateView(view));
    });
    this.destroyRef.onDestroy(() => {
      this.destroyed = true;
      this.key = null;
      this.invalidate(true);
    });
  }

  private invalidate(clearCursors: boolean): void {
    this.epoch += 1;
    this.lifetime.abort();
    this.lifetime = new AbortController();
    clearTimeout(this.retryTimer);
    clearTimeout(this.leaseTimer);
    clearTimeout(this.readyTimer);
    this.retryTimer = this.leaseTimer = this.readyTimer = undefined;
    this.feed?.close();
    this.feed = null;
    const seed = this.seed;
    this.seed = null;
    if (seed) void seed.close().catch(() => undefined);
    if (clearCursors || this.nativeAccounts === null) {
      this.nativeSessionEpoch += 1;
      this.nativeAccounts = null;
      void this.coordinator.stop().catch(() => undefined);
    }
    if (clearCursors) {
      this.nativeIdentityEpoch += 1;
      this.cursors.clear();
      this.retryDelay = RETRY_MIN_MS;
    }
  }

  private identityCurrent(): boolean {
    return (
      !this.destroyed &&
      this.key !== null &&
      this.authIdentity()?.accountId === this.key &&
      this.currentUser.accountId() === this.key
    );
  }

  private current(epoch: number): boolean {
    return this.identityCurrent() && this.epoch === epoch;
  }

  private async openNative(): Promise<NotificationSession | null> {
    const nativeEpoch = ++this.nativeSessionEpoch;
    let settled = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const work = this.desktopPresenter.open().then((session) => {
      if (settled && session) {
        void session.close().catch(() => undefined);
        return null;
      }
      return session
        ? boundedNativeSession(session, () => {
            if (!this.identityCurrent() || nativeEpoch !== this.nativeSessionEpoch) return;
            this.nativeAccounts = null;
            this.retry(this.epoch);
          })
        : null;
    });
    try {
      return await Promise.race([
        work,
        new Promise<null>((resolve) => {
          timer = setTimeout(() => {
            settled = true;
            resolve(null);
          }, NATIVE_NEGOTIATION_TIMEOUT_MS);
        }),
      ]);
    } finally {
      settled = true;
      clearTimeout(timer);
    }
  }

  private async connect(epoch: number): Promise<void> {
    if (!this.current(epoch)) return;
    if (this.nativeAccounts === null) {
      const native = await this.openNative();
      if (!this.current(epoch)) {
        if (native) await native.close();
        return;
      }
      // Browser, obsolete bridge, unsupported native platform: no HTTP/socket work, no permission prompt.
      if (!native) return;
      this.seed = native;
    }
    const signal = this.lifetime.signal;
    const ticket = await firstValueFrom(
      this.http
        .post<unknown>(
          environment.baseUrl + '/api/account/sessions/notifications/ticket',
          {},
          {
            withCredentials: true,
            context: new HttpContext().set(IGNORE_UNAUTHORIZED_REDIRECT, true),
          },
        )
        .pipe(takeUntil(fromEvent(signal, 'abort')), timeout(6_000)),
    );
    if (!this.current(epoch)) return;
    if (
      !notificationRecord(ticket) ||
      !notificationKeys(ticket, ['version', 'ticket', 'expiresAt']) ||
      ticket['version'] !== 1 ||
      typeof ticket['ticket'] !== 'string' ||
      !/^[A-Za-z0-9_-]{43}$/.test(ticket['ticket']) ||
      !notificationDate(ticket['expiresAt']) ||
      Date.parse(ticket['expiresAt']) <= Date.now()
    )
      throw new Error('Notification connection unavailable');
    const socket = io(environment.baseUrl + '/notifications-v1', {
      path: '/notifications-v1/socket.io',
      transports: ['websocket'],
      withCredentials: true,
      reconnection: false,
      autoConnect: false,
      auth: { version: 1, ticket: ticket['ticket'] },
    });
    const port: HostedNotificationSocket = {
      on: (event, listener) => {
        socket.on(event, listener);
      },
      emit: (event, data) => {
        socket.emit(event, data);
      },
      disconnect: () => {
        socket.disconnect();
        socket.removeAllListeners();
      },
    };
    this.feed = new NotificationFeedClient({
      socket: port,
      cursors: this.cursors,
      ready: async (ready) => {
        if (!this.current(epoch)) return false;
        const accounts = ready.recipients.map((recipient) => recipient.accountId);
        const sameIdentity =
          this.nativeAccounts !== null &&
          this.nativeAccounts.length === accounts.length &&
          accounts.every((accountId) => this.nativeAccounts?.includes(accountId));
        if (!sameIdentity) {
          if (this.nativeAccounts !== null) this.nativeSessionEpoch += 1;
          this.nativeAccounts = null;
          const started = await this.coordinator.start({
            generation: `hosted-${this.nativeIdentityEpoch}`,
            scopeIds: accounts,
          });
          if (!this.current(epoch) || !started) return false;
          this.nativeAccounts = accounts;
        }
        if (!this.current(epoch)) return false;
        clearTimeout(this.readyTimer);
        this.coordinator.updateView(this.view.notificationView());
        this.retryDelay = RETRY_MIN_MS;
        clearTimeout(this.leaseTimer);
        // Re-enter through a fresh one-use ticket before the lease expires. Renewal is quiet too.
        this.leaseTimer = setTimeout(
          () => this.retry(epoch, true),
          Math.max(1, Math.min(300_000, Date.parse(ready.leaseExpiresAt) - Date.now()) - 10_000),
        );
        void this.injector
          .get(HostedNotificationActionsService)
          .resume(signal)
          .catch(() => undefined);
        return true;
      },
      event: (event) => {
        if (!this.current(epoch)) return;
        // Pull the latest hint synchronously too: a mounted view can become unusable before the
        // next Angular effect flush, and an old hint must not suppress this banner.
        void this.coordinator.updateView(this.view.notificationView());
        const candidate: NotificationCandidate = {
          version: 1,
          id: `${event.accountId}:${event.deliveryId}`,
          dedupeId: event.deliveryId,
          scopeId: event.accountId,
          source: 'chat',
          category: 'direct-message',
          expiresAt: Date.parse(event.expiresAt),
          payload: event,
        };
        for (const listener of this.listeners) listener(candidate);
      },
      removed: (accountId) => {
        if (this.nativeAccounts) this.nativeAccounts = this.nativeAccounts.filter((account) => account !== accountId);
        void this.coordinator.removeScope(accountId).catch(() => undefined);
      },
      unavailable: () => this.retry(epoch),
    });
    this.readyTimer = setTimeout(() => this.retry(epoch), 10_000);
    socket.connect();
  }

  private retry(epoch: number, renewal = false): void {
    if (!this.current(epoch)) return;
    this.invalidate(false);
    const nextEpoch = this.epoch;
    const delay = renewal ? 0 : this.retryDelay;
    this.retryDelay = Math.min(RETRY_MAX_MS, this.retryDelay * 2);
    this.retryTimer = setTimeout(() => {
      if (!this.current(nextEpoch)) return;
      void this.connect(nextEpoch).catch(() => this.retry(nextEpoch));
    }, delay);
  }
}
